import { type CSSProperties, type ReactNode } from "react";
import { type BBNode } from "../engine/bbcode";

interface RichTextProps {
  nodes: BBNode[];
  revealedCount: number;
}

const SIZE_MAP: Record<string, string> = {
  small: "0.85em",
  normal: "1em",
  large: "1.3em",
};

export function RichText({ nodes, revealedCount }: RichTextProps) {
  let revealed = 0;

  function renderNodes(nodeList: BBNode[]): ReactNode[] {
    const result: ReactNode[] = [];
    for (let i = 0; i < nodeList.length; i++) {
      const node = nodeList[i];
      if (node.type === "text") {
        const remaining = revealedCount - revealed;
        if (remaining <= 0) {
          result.push(
            <span key={i} style={{ opacity: 0 }}>{node.text}</span>,
          );
        } else if (remaining >= node.text.length) {
          result.push(<span key={i}>{node.text}</span>);
        } else {
          result.push(
            <span key={i}>
              {node.text.slice(0, remaining)}
              <span style={{ opacity: 0 }}>{node.text.slice(remaining)}</span>
            </span>,
          );
        }
        revealed += node.text.length;
      } else {
        const style = tagStyle(node.tag, node.attr);
        const className = tagClassName(node.tag);
        const children = renderNodes(node.children);
        result.push(
          <span key={i} style={style} className={className}>
            {children}
          </span>,
        );
      }
    }
    return result;
  }

  return <>{renderNodes(nodes)}</>;
}

function tagStyle(tag: string, attr?: string): CSSProperties {
  switch (tag) {
    case "b":
      return { fontWeight: "bold" };
    case "i":
      return { fontStyle: "italic" };
    case "color":
      return attr ? { color: attr } : {};
    case "size": {
      const size = attr ? (SIZE_MAP[attr] ?? `${attr}em`) : undefined;
      return size ? { fontSize: size } : {};
    }
    default:
      return {};
  }
}

function tagClassName(tag: string): string | undefined {
  if (tag === "shake") return "bbcode-shake";
  if (tag === "wave") return "bbcode-wave";
  return undefined;
}
